import React, { useState, useRef, useEffect } from 'react';
import { SidebarProps as Props } from '..';
import { useSelector } from 'react-redux';
import { useAppAndBranchId, useQuery } from '../../../../hooks';
import { AppState } from '../../../../../../../../stateManager/store';
import { api } from '../../api';
import style from '../new.module.scss';
import { Select } from '../../../../components';
import { responseTypes } from '../../constants';

type NewActionProps = {
	close: () => void;
  viewMessageResp: (visibility: boolean) => void
	initialValues?: {
		flowName?: string;
		nodeId?: string;
		type?: string;
		actionName?: string;
	};
} & Props;

export function NewAction(props: NewActionProps) {
	const [appId] = useAppAndBranchId();
	const appVersion = useSelector(
		(state: AppState) => state.apps.appVersion && state.apps.appVersion[appId],
	);
	const versionId = appVersion && appVersion.versionId;
	const { close, updateDialogByKey, dialog, initialValues = {} } = props;
	const actionKey = useRef(initialValues.actionName ?? '');
	const [actionName, setActionName] = useState(initialValues?.actionName ?? '');
	const [type, setType] = useState(initialValues.type ?? '');
	const [flowName, setFlowName] = useState(initialValues.flowName ?? '');
	const [nodeId, setNodeId] = useState(initialValues.nodeId ?? '');

  const messages = actionKey.current ? (dialog.actions[actionKey.current] as any).messages : props.newAction.messages

	const flowId = props.flows?.find((item) => item.value === flowName)?.id ?? '';
	const { data: nodesRes } = useQuery(
		() => api.getNodes(appId, versionId, flowId),
		flowId,
		Boolean(flowId),
	);

  const [errors, setErrors] = useState({
    type: "",
    actionName: "",
    flowName: "",
    nodeId: "",
    messages: "",
  })

  function validate() {
    const actionKeys = Object.keys(dialog.actions).filter(key => key !== actionKey.current)
		const errors: Record<string, string> = {};
		const formatter = (prefix: string) => `${prefix} shouldn't be empty`;
    // check for duplicate action name
		if (!actionName.trim().length) {
			errors['actionName'] = formatter('Name');
		}
    if(actionKeys.includes(actionName.trim())) {
			errors['actionName'] = "Name already exists"
    }
		if (!type.trim().length) {
			errors['type'] = formatter('Response type');
		}
		if (type === 'flow' && !flowName.trim().length) {
			errors['flowName'] = formatter('Flow Name');
		}
		if (type === 'message') {
      const messages = actionKey.current ? (dialog.actions[actionKey.current] as any).messages : props.newAction.messages
      if(!messages?.length) {
        errors['messages'] = formatter('Messages');
      }
		}
		setErrors((prev) => ({ ...prev, ...errors }));
		return Object.keys(errors).length === 0;
  }

  function clearError(key: keyof typeof errors) {
    setErrors(prev => ({...prev, [key]: ""}))
  }

	function handleAdd() {
   if(!validate()) {
     return
   }

    let newAction: Record<string, any> = {
      type
    }
    if(type === 'flow') {
      newAction.nodeId = nodeId || undefined
      newAction.flowName = flowName
    }
    if(type === 'message') {
      const action: any = dialog.actions[actionKey.current]
      if(actionKey.current) {
        newAction.isRandom = action.isRandom ?? false
        newAction.messages = action.messages ?? []
        newAction.suggestions = action.suggestions ?? []
      } else {
        newAction = { ...newAction, ...props.newAction }
      }
    }
    if(actionKey.current === actionName) {
      updateDialogByKey("actions", {
        ...dialog.actions,
        [actionName]: newAction
      })
      close()
      return
    }

    // If the action key is changed, delete the old action
    const actions = { ...dialog.actions }
    delete actions[actionKey.current]
    actions[actionName] = newAction as any
    updateDialogByKey("actions", actions)
    close()
	}

  // if the action is of type "message", show the `MessageResp` component
  // on mount
	useEffect(() => {
    if(actionKey.current && type === 'message') {
      props.selectAction(actionKey.current)
      props.viewMessageResp(true)
    }
	}, [])

	return (
		<div className={style.newAction}>
			<div className={style.panel}>
				<div>
					<label htmlFor="action" className={style.label}>
						Action Name
					</label>
					<input
						value={actionName}
						onChange={(e) => {
              clearError("actionName")
              setActionName(e.target.value)
            }}
						id="action"
						className={style.input}
						placeholder="Enter action name"
					/>
          {errors.actionName?.length > 0 && (
            <span className={style.error}>{errors.actionName}</span>
          )}
				</div>

				<div>
					<label htmlFor="response" className={style.label}>
						Response
					</label>
					<Select
						placeholder="Select response type"
						options={responseTypes.filter((el) => el.value !== 'service')}
						onSelect={(value) => {
              clearError("type")
              setType(value)
              if(value === 'message') {
                props.viewMessageResp(true)
                if(actionKey.current) {
                  const action = dialog.actions[actionKey.current]
                  if(!("messages" in action)) {
                    updateDialogByKey("actions", {
                      ...dialog.actions,
                      [actionKey.current]: {
                       ...action,
                       isRandom: false,
                       messages: [],
                       suggestions: [],
                      }
                    })
                  }
                }
                props.selectAction(actionKey.current)
              } else {
                props.viewMessageResp(false)
              }
            }}
						value={type}
					/>
          {errors.type?.length > 0 && (
            <span className={style.error}>{errors.type}</span>
          )}
          {type === "message" && !messages?.length && errors.messages?.length > 0 && (
            <span className={style.error}>{errors.messages}</span>
          )}
				</div>

				{type === 'flow' && (
					<>
						<div>
							<label htmlFor="flows" className={style.label}>
								Flow
							</label>
							<Select
								placeholder="Select flow"
								options={props.flows}
								onSelect={(value) => {
                  clearError("flowName")
                  setFlowName(value)
                  setNodeId("")
                }}
								value={flowName}
							/>
              {errors.flowName?.length > 0 && (
                <span className={style.error}>{errors.flowName}</span>
              )}
						</div>
						<div>
							<label htmlFor="startNode" className={style.label}>
								Start Node
							</label>
							<Select
								placeholder="Default Start Node"
								options={[{label: "Default Start Node", value: "DEFAULT"}, ...nodesRes?.nodes ?? []]}
								onSelect={(value) => value == "DEFAULT" ? setNodeId("") : setNodeId(value)}
								value={nodeId}
							/>
              {!nodeId && errors.nodeId?.length > 0 && (
                <span className={style.error}>{errors.nodeId}</span>
              )}
						</div>
					</>
				)}
			</div>
			<div className={style.actions}>
				<button onClick={close}>Cancel</button>
				<button onClick={handleAdd}>
					{actionKey.current ? 'Update' : 'Add'}
				</button>
			</div>
		</div>
	);
}
